/**
 * /operations/eos/final-settlements — Session 3B PR 10.
 *
 * Owning agent: Rawatib. Disputed register + paid-this-period view.
 */

import { useQuery } from "@tanstack/react-query";
import {
  AlertOctagon,
  CheckCircle2,
  ChevronRight,
  PiggyBank,
  Plus,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { InlineAISuggestion, PageHeader, TraceableValue } from "../components/ui";
import { useLegalEntityGate } from "../lib/legalEntity";
import { session3bApi } from "../lib/session3b-api";
import type { FinalSettlementItem } from "../lib/session3b-api";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { showAlertTrace } from "./_session3aHelpers";

type StateFilter = "all" | "draft" | "approved" | "paid" | "disputed";

function _statusToneClass(s: FinalSettlementItem["status"]): string {
  if (s === "paid") return "bg-emerald-100 text-emerald-800";
  if (s === "disputed") return "bg-rose-100 text-rose-800";
  if (s === "approved") return "bg-brand-blue/15 text-brand-deep";
  return "bg-slate-100 text-slate-700";
}

function _fmtSar(n: number): string {
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function EosFinalSettlementsPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [stateFilter, setStateFilter] = useState<StateFilter>("all");

  const list = useQuery({
    queryKey: ["session3b-final-settlements", legalEntityId, stateFilter],
    queryFn: () =>
      session3bApi.listFinalSettlements(
        legalEntityId ?? "",
        stateFilter === "all" ? undefined : stateFilter,
      ),
    enabled: !!legalEntityId,
  });

  const items: FinalSettlementItem[] = list.data?.items ?? [];

  const counts = useMemo(() => {
    const unpaid = items.filter((r) => r.status === "draft" || r.status === "approved");
    const oldestUnpaid = unpaid.reduce<FinalSettlementItem | undefined>(
      (prev, r) =>
        r.days_from_termination_to_payment !== null &&
        (prev?.days_from_termination_to_payment ?? -1) <
          (r.days_from_termination_to_payment ?? 0)
          ? r
          : prev,
      undefined,
    );
    const sumUnpaid = unpaid.reduce((a, r) => a + r.settlement_amount_sar, 0);
    return {
      total: items.length,
      attention: unpaid.length,
      critical: items.filter((r) => r.status === "disputed").length,
      sumUnpaid,
      oldestUnpaid,
    };
  }, [items]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("eos.final_settlements.title", "Final Settlements")}
          subtitle={t(
            "eos.final_settlements.subtitle",
            "Disputed register · paid-this-period · final payslip status. Rawatib tracks the Article 88 14-day payment window.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {(["all", "draft", "approved", "paid", "disputed"] as StateFilter[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setStateFilter(f)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  stateFilter === f
                    ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
                    : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
                }`}
              >
                {t(`eos.final_settlements.filter.${f}`, f)}
                {f === "disputed" && counts.critical > 0 && (
                  <span className="ms-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-600 px-1 text-[9px] font-semibold text-white">
                    {counts.critical}
                  </span>
                )}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() =>
              showAlertTrace(
                t(
                  "eos.final_settlements.cta.initiate_note",
                  "Initiate Final Settlement drawer: pick a termination → run settlement calc → route for approval.",
                ),
              )
            }
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <Plus className="h-4 w-4" />
            {t("eos.final_settlements.cta.initiate", "Initiate Final Settlement")}
          </button>
        </div>

        {counts.attention > 0 && counts.oldestUnpaid && (
          <InlineAISuggestion
            agent="rawatib"
            icon={<AlertOctagon className="h-4 w-4" />}
            severity="warning"
            headline={t(
              "eos.final_settlements.suggestion.unpaid_headline",
              `${counts.attention} unpaid settlement${counts.attention === 1 ? "" : "s"} · oldest ${counts.oldestUnpaid.days_from_termination_to_payment ?? "?"} days old.`,
            )}
            body={t(
              "eos.final_settlements.suggestion.unpaid_body",
              "Article 88 mandates settlement within ~14 days of termination. Rawatib suggests processing the oldest first to avoid timeline breaches.",
            )}
            primaryAction={{
              label: t("eos.final_settlements.suggestion.show_unpaid", "Show unpaid"),
              onClick: () => setStateFilter("approved"),
            }}
            suggestionKey={`final-unpaid-${counts.oldestUnpaid.settlement_id}`}
          />
        )}
        {counts.total === 0 && !list.isLoading && (
          <InlineAISuggestion
            agent="rawatib"
            icon={<CheckCircle2 className="h-4 w-4" />}
            severity="info"
            headline={t(
              "eos.final_settlements.suggestion.empty_headline",
              "No final settlements on file.",
            )}
            body={t(
              "eos.final_settlements.suggestion.empty_body",
              "Rawatib notes: settlements appear here when the M18 workflow advances past 'approved'. Convert an EOS calculation draft to start one.",
            )}
            primaryAction={{
              label: t("eos.final_settlements.suggestion.open_drafts", "Open EOS drafts"),
              onClick: () => window.location.assign("/operations/eos/calculation-drafts"),
            }}
            suggestionKey="final-empty"
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {items.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("eos.final_settlements.col.employee", "Employee")}</th>
                  <th className="px-3 py-2">{t("eos.final_settlements.col.amount", "Settlement (SAR)")}</th>
                  <th className="px-3 py-2">{t("eos.final_settlements.col.status", "Status")}</th>
                  <th className="px-3 py-2">{t("eos.final_settlements.col.generated", "Generated")}</th>
                  <th className="px-3 py-2">{t("eos.final_settlements.col.paid", "Paid")}</th>
                  <th className="px-3 py-2">{t("eos.final_settlements.col.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.settlement_id} className="group border-t border-border-subtle hover:bg-surface-sunken/40">
                    <td className="px-3 py-2">
                      <Link
                        to={`/people/employees/${row.employee_id}`}
                        className="font-medium text-[#2260C9] hover:underline"
                      >
                        {row.employee_name_en}
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "eos.final_settlements.trace.amount",
                              `settlement_amount_sar = M18 FinalSettlement.total_settlement_amount = gratuity (Article 84/85) + leave_encashment + accrued allowances − outstanding loans. Re-computable at view time.`,
                            ),
                          )
                        }
                      >
                        <span className="font-mono">{_fmtSar(row.settlement_amount_sar)}</span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${_statusToneClass(row.status)}`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      <span dir="ltr">{row.generated_at}</span>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      <span dir="ltr">{row.paid_at ?? "—"}</span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1.5 opacity-50 transition-opacity group-hover:opacity-100">
                        <Link
                          to={`/operations/eos/${row.termination_id}`}
                          className="inline-flex items-center gap-1 rounded border border-brand-blue px-1.5 py-0.5 text-[10px] font-medium text-[#2260C9] hover:bg-brand-blue/10"
                        >
                          {t("eos.final_settlements.action.view", "View")}
                          <ChevronRight className="h-3 w-3" />
                        </Link>
                        {row.status === "approved" && (
                          <button
                            type="button"
                            onClick={() =>
                              showAlertTrace(
                                t(
                                  "eos.final_settlements.action.process_note",
                                  "Process payment → final payslip generation + bank-file batch + status='paid'.",
                                ),
                              )
                            }
                            className="inline-flex items-center gap-1 rounded border border-emerald-300 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-50"
                          >
                            <PiggyBank className="me-1 inline h-3 w-3" />
                            {t("eos.final_settlements.action.process", "Process payment")}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="rawatib"
          liveContext={{
            total: counts.total,
            attention: counts.attention,
            critical: counts.critical,
            top_kind: t("eos.final_settlements.title", "Final Settlements"),
            window_label: `unpaid SAR ${_fmtSar(counts.sumUnpaid)}`,
          }}
        />
      </aside>
    </div>
  );
}
